// src/features/operational/services/teachingService.js
import { 
    collection, 
    doc, 
    getDoc, 
    getDocs, 
    addDoc, 
    updateDoc, 
    deleteDoc,
    query, 
    where, 
    orderBy, 
    serverTimestamp 
  } from "firebase/firestore";
  import { 
    ref, 
    uploadBytes, 
    getDownloadURL,
    deleteObject 
  } from "firebase/storage";
  import { db, storage } from "../../../firebase/config";
  
  // Fetch teaching lessons
  export const fetchTeachingLessons = async (userId) => {
    try {
      const lessonsRef = collection(db, "lessons");
      const q = query(
        lessonsRef,
        where("userId", "==", userId),
        orderBy("date", "desc"),
        orderBy("time", "asc")
      );
      
      const querySnapshot = await getDocs(q);
      const lessons = [];
      
      for (const docSnapshot of querySnapshot.docs) {
        const lesson = { id: docSnapshot.id, ...docSnapshot.data() };
        
        // Get subject info
        if (lesson.subjectId) {
          const subjectDoc = await getDoc(doc(db, "subjects", lesson.subjectId));
          if (subjectDoc.exists()) {
            lesson.subject = { id: subjectDoc.id, ...subjectDoc.data() };
          }
        }
        
        lessons.push(lesson);
      }
      
      return lessons;
    } catch (error) {
      console.error("Error fetching lessons:", error);
      throw error;
    }
  };
  
  // Fetch subjects
  export const fetchSubjects = async (userId) => {
    try {
      const subjectsRef = collection(db, "subjects");
      const q = query(
        subjectsRef,
        where("userId", "==", userId),
        orderBy("name")
      );
      
      const querySnapshot = await getDocs(q);
      const subjects = [];
      
      querySnapshot.forEach((doc) => {
        subjects.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return subjects;
    } catch (error) {
      console.error("Error fetching subjects:", error);
      throw error;
    }
  };
  
  // Fetch ATP items for a subject
  export const fetchSubjectPlans = async (userId, subjectId) => {
    try {
      const plansRef = collection(db, "annualTeachingPlanItems");
      const q = query(
        plansRef,
        where("userId", "==", userId),
        where("subject", "==", subjectId),
        orderBy("plannedStart", "asc")
      );

      const querySnapshot = await getDocs(q);
      const plans = [];

      querySnapshot.forEach((doc) => {
        plans.push({
          id: doc.id,
          ...doc.data(),
          name: doc.data().topic
        });
      });

      return plans;
    } catch (error) {
      console.error("Error fetching ATP items for subject:", error);
      throw error;
    }
  };
  
  // Fetch subject resources
  export const fetchSubjectResources = async (subjectId) => {
    try {
      const resourcesRef = collection(db, "subjects", subjectId, "resources");
      const q = query(resourcesRef, orderBy("createdAt", "desc"));
      
      const querySnapshot = await getDocs(q);
      const resources = [];
      
      querySnapshot.forEach((doc) => {
        resources.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return resources;
    } catch (error) {
      console.error("Error fetching subject resources:", error);
      throw error;
    }
  };
  
  // Fetch learners by grade
  export const fetchLearners = async (grade) => {
    try {
      if (!grade) return [];
      
      const learnersRef = collection(db, "learners");
      const q = query(
        learnersRef,
        where("grade", "==", grade),
        orderBy("lastName"),
        orderBy("firstName")
      );
      
      const querySnapshot = await getDocs(q);
      const learners = [];
      
      querySnapshot.forEach((doc) => {
        const learner = doc.data();
        learners.push({
          id: doc.id,
          name: `${learner.firstName} ${learner.lastName}`,
          ...learner
        });
      });
      
      return learners;
    } catch (error) {
      console.error("Error fetching learners:", error);
      // For demo purposes, return mock data if no learners exist in database
      return generateMockLearners(grade);
    }
  };
  
  // Create lesson
  export const createLesson = async (userId, lessonData) => {
    try {
      let lessonPlanUrl = null;
      let lessonPlanPath = null;
      
      // Upload lesson plan if provided
      if (lessonData.lessonPlan && lessonData.lessonPlan instanceof File) {
        const fileExtension = lessonData.lessonPlan.name.split('.').pop();
        const fileName = `${Date.now()}.${fileExtension}`;
        const filePath = `users/${userId}/lessons/plans/${fileName}`;
        
        const storageRef = ref(storage, filePath);
        await uploadBytes(storageRef, lessonData.lessonPlan);
        
        lessonPlanUrl = await getDownloadURL(storageRef);
        lessonPlanPath = filePath;
      }
      
      // Create a clean lesson object to store in Firestore
      const lesson = {
        userId,
        date: lessonData.date,
        time: lessonData.time,
        term: lessonData.term,
        subjectId: lessonData.subject,
        grade: lessonData.grade,
        atpItemId: lessonData.atpItem || null,
        resourceId: lessonData.resource || null,
        topic: lessonData.topic,
        lessonPlanUrl: lessonPlanUrl,
        lessonPlanPath: lessonPlanPath,
        assessmentType: lessonData.assessmentType || null,
        hasHomework: lessonData.hasHomework,
        homeworkDescription: lessonData.homeworkDescription || null,
        expectedAttendance: parseInt(lessonData.expectedAttendance) || 0,
        actualAttendance: parseInt(lessonData.actualAttendance) || 0,
        absentees: lessonData.absentees.map(learner => ({
          id: learner.id,
          name: learner.name
        })),
        periodRegister: lessonData.periodRegister,
        learnerEngagement: lessonData.learnerEngagement,
        lessonProgress: lessonData.lessonProgress,
        atpStatus: lessonData.atpStatus,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, "lessons"), lesson);
      
      return {
        id: docRef.id,
        ...lesson
      };
    } catch (error) {
      console.error("Error creating lesson:", error);
      throw error;
    }
  };
  
  // Update lesson
  export const updateLesson = async (lessonId, userId, lessonData) => {
    try {
      const lessonRef = doc(db, "lessons", lessonId);
      const lessonDoc = await getDoc(lessonRef);
      
      if (!lessonDoc.exists()) {
        throw new Error("Lesson not found");
      }
      
      const lessonSnapshot = lessonDoc.data();
      let lessonPlanUrl = lessonSnapshot.lessonPlanUrl;
      let lessonPlanPath = lessonSnapshot.lessonPlanPath;
      
      // Handle file upload/replacement if needed
      if (lessonData.lessonPlan && lessonData.lessonPlan instanceof File) {
        // Delete old file if exists
        if (lessonPlanPath) {
          const oldFileRef = ref(storage, lessonPlanPath);
          try {
            await deleteObject(oldFileRef);
          } catch (error) {
            console.warn("Error deleting old lesson plan:", error);
          }
        }
        
        // Upload new file
        // Upload new file
      const fileExtension = lessonData.lessonPlan.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExtension}`;
      const filePath = `users/${userId}/lessons/plans/${fileName}`;
      
      const storageRef = ref(storage, filePath);
      await uploadBytes(storageRef, lessonData.lessonPlan);
      
      lessonPlanUrl = await getDownloadURL(storageRef);
      lessonPlanPath = filePath;
    } else if (lessonData.lessonPlan === null && lessonPlanPath) {
      // Delete file if it was removed
      const oldFileRef = ref(storage, lessonPlanPath);
      try {
        await deleteObject(oldFileRef);
      } catch (error) {
        console.warn("Error deleting old lesson plan:", error);
      }
      lessonPlanUrl = null;
      lessonPlanPath = null;
    }
    
    // Create update object
    const updateData = {
      date: lessonData.date,
      time: lessonData.time,
      term: lessonData.term,
      subjectId: lessonData.subject,
      grade: lessonData.grade,
      atpItemId: lessonData.atpItem || null,
      resourceId: lessonData.resource || null,
      topic: lessonData.topic,
      lessonPlanUrl: lessonPlanUrl,
      lessonPlanPath: lessonPlanPath,
      assessmentType: lessonData.assessmentType || null,
      hasHomework: lessonData.hasHomework,
      homeworkDescription: lessonData.homeworkDescription || null,
      expectedAttendance: parseInt(lessonData.expectedAttendance) || 0,
      actualAttendance: parseInt(lessonData.actualAttendance) || 0,
      absentees: lessonData.absentees.map(learner => ({
        id: learner.id,
        name: learner.name
      })),
      periodRegister: lessonData.periodRegister,
      learnerEngagement: lessonData.learnerEngagement,
      lessonProgress: lessonData.lessonProgress,
      atpStatus: lessonData.atpStatus,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(lessonRef, updateData);
    
    return {
      id: lessonId,
      ...updateData
    };
  } catch (error) {
    console.error("Error updating lesson:", error);
    throw error;
  }
};

// Delete lesson
export const deleteLesson = async (lessonId) => {
  try {
    const lessonRef = doc(db, "lessons", lessonId);
    const lessonDoc = await getDoc(lessonRef);
    
    if (!lessonDoc.exists()) {
      throw new Error("Lesson not found");
    }
    
    const lessonData = lessonDoc.data();
    
    // Delete lesson plan file if exists
    if (lessonData.lessonPlanPath) {
      const fileRef = ref(storage, lessonData.lessonPlanPath);
      try {
        await deleteObject(fileRef);
      } catch (error) {
        console.warn("Error deleting lesson plan file:", error);
      }
    }
    
    // Delete lesson document
    await deleteDoc(lessonRef);
    
    return { success: true };
  } catch (error) {
    console.error("Error deleting lesson:", error);
    throw error;
  }
};

// Helper function for demo purposes - generates mock learners
function generateMockLearners(grade) {
  // First names
  const firstNames = [
    'Emma', 'Liam', 'Olivia', 'Noah', 'Ava', 'Ethan', 'Sofia', 'Lucas',
    'Isabella', 'Mason', 'Mia', 'Oliver', 'Zoe', 'Jacob', 'Lily', 'Logan',
    'Chloe', 'Aiden', 'Madison', 'Jackson', 'Emily', 'Amelia', 'Sipho',
    'Thabo', 'Lerato', 'Nandi', 'Thabiso', 'Phumzile', 'Neo', 'Lesedi'
  ];
  
  // Last names
  const lastNames = [
    'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller',
    'Davis', 'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez',
    'Wilson', 'Anderson', 'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin',
    'Dlamini', 'Nkosi', 'Ndlovu', 'Khumalo', 'Mokoena', 'Molefe', 'Mkhize',
    'Mbatha', 'Sithole', 'Mabaso'
  ];
  
  // Generate 20-30 random learners
  const learnerCount = Math.floor(Math.random() * 11) + 20; // 20-30 learners
  const learners = [];
  
  for (let i = 0; i < learnerCount; i++) {
    const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
    
    learners.push({
      id: `learner-${i + 1}`,
      firstName,
      lastName,
      name: `${firstName} ${lastName}`,
      grade
    });
  }
  
  return learners;
}
